import { Injectable } from "@angular/core";

@Injectable({ providedIn: 'root' })
export class OnlyAcceptNumbers {

  // Function responsible for making the input accept 
  // only numbers with a maximum of three numbers
  formatNumber(event: Event) {

    const input = event.target as HTMLInputElement;
    const value = input.value;
    const valueFormat = value.replace(/[^0-9]/g, '');

    input.value = valueFormat;

    return Number(valueFormat);
  }

  // Function responsible for formatting the price for the input and database
  // Handles different currency formats: R$ uses pt-BR (comma for decimal), $ uses en-US (dot for decimal)
  formatPriceForInputAndDatabase(event: Event, currency?: string) {

    const input = event.target as HTMLInputElement;
    let priceTreatment = input.value;

    // Remove non-digit characters first
    priceTreatment = priceTreatment.replace(/\D/g, "");

    // Add decimal separator based on currency format
    // R$ uses pt-BR format: comma for decimal, dot for thousands (1.234,56)
    // $ uses en-US format: dot for decimal, comma for thousands (1,234.56) → but we need (1234.56) for DB
    if (currency === 'R$') {
      // Format as 1.234,56 (Brazilian Real)
      priceTreatment = priceTreatment.replace(/(\d+)(\d{2})$/, "$1,$2");
      priceTreatment = priceTreatment.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
    } else {
      // Format as 1,234.56 (US Dollar / Peso) for display, then convert to DB format
      priceTreatment = priceTreatment.replace(/(\d+)(\d{2})$/, "$1.$2");
      priceTreatment = priceTreatment.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    }

    input.value = priceTreatment;

    // Convert to database format (always dot for decimal, no thousand separators)
    let valueForDatabase = priceTreatment;
    if (currency === 'R$') {
      // pt-BR format (1.234,56) → remove dots, replace comma with dot → 1234.56
      valueForDatabase = valueForDatabase.replace(/\./g, '').replace(',', '.');
    } else {
      // en-US format (1,234.56) → remove commas, keep dot → 1234.56
      valueForDatabase = valueForDatabase.replace(/,/g, '');
    }

    return valueForDatabase;
  }

  // Function responsible for formatting the price that 
  // will be displayed on the page according to the currency
  formattingFrontPrice(price: number, currency: string) {

    const numeroComDuasCasas = Number(price);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let formatter: any;

    if (currency === 'R$') {

      formatter = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 })
    } else {

      formatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 })
    }

    return formatter.format(numeroComDuasCasas);
  }

  /**
   * Converts a formatted display price string back to a numeric value.
   * This is essential when currency changes to properly handle user edits.
   * 
   * Example:
   * - Input: "191.403,00" (pt-BR format for R$), currency = "R$" → Output: 191403.00
   * - Input: "191,403.00" (en-US format for $), currency = "$" → Output: 191403.00
   */
  parseFormattedPriceToNumber(displayPrice: string, currency: string): number {
    if (!displayPrice || displayPrice.trim() === '') {
      return 0;
    }

    let cleanPrice = displayPrice.trim();

    if (currency === 'R$') {
      // pt-BR format: 191.403,00 → remove dots (thousands), replace comma with dot → 191403.00
      cleanPrice = cleanPrice.replace(/\./g, '').replace(',', '.');
    } else {
      // en-US format: 191,403.00 → remove commas (thousands), keep dot → 191403.00
      cleanPrice = cleanPrice.replace(/,/g, '');
    }

    const numericValue = parseFloat(cleanPrice);
    return isNaN(numericValue) ? 0 : numericValue;
  }

  /**
   * Detects the currency format of a display price by analyzing its pattern.
   * 
   * Logic:
   * - If last separator is COMMA and has exactly 2 digits after it → R$ format (1.234,56)
   * - If last separator is DOT and has exactly 2 digits after it → $ format (1,234.56)
   * - Mixed or missing → Default to $ format (en-US)
   * 
   * Examples:
   * - "3.254,00" → R$ (comma is decimal)
   * - "3,254.00" → $ (dot is decimal)
   * - "100,00" → R$
   * - "100.00" → $
   * - "1000" → $ (default)
   */
  detectCurrencyFormat(displayPrice: string): string {
    if (!displayPrice || displayPrice.trim() === '') {
      return '$'; // Default to en-US
    }

    const trimmed = displayPrice.trim();
    
    // Find the position of last dot and last comma
    const lastDotIndex = trimmed.lastIndexOf('.');
    const lastCommaIndex = trimmed.lastIndexOf(',');
    
    // Check which separator appears last (rightmost)
    if (lastCommaIndex > lastDotIndex) {
      // Comma is rightmost
      const afterComma = trimmed.substring(lastCommaIndex + 1);
      // If exactly 2 digits after comma, it's the decimal separator (R$ format)
      if (afterComma.length === 2 && /^\d+$/.test(afterComma)) {
        return 'R$'; // pt-BR format: last separator is comma (decimal)
      }
    } else if (lastDotIndex > lastCommaIndex) {
      // Dot is rightmost
      const afterDot = trimmed.substring(lastDotIndex + 1);
      // If exactly 2 digits after dot, it's the decimal separator ($ format)
      if (afterDot.length === 2 && /^\d+$/.test(afterDot)) {
        return '$'; // en-US format: last separator is dot (decimal)
      }
    }
    
    // Default to $ format if pattern doesn't match expected separators
    return '$';
  }

}
